import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Autorenew as SubscriptionIcon,
  Apple as AppleIcon,
  Google as GoogleIcon,
  CardGiftcard as CreditsIcon,
} from '@mui/icons-material';
import { adminBillingApi, type PaymentSettings, type PaymentSettingsResponse } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '../../hooks/core';
import { isAdmin } from '../../types';

export function PaymentSettingsPage() {
  const { user } = useAuth();

  useDocumentTitle('Payment Settings');
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [canSwitchMode, setCanSwitchMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Mode switch dialog
  const [modeSwitchDialogOpen, setModeSwitchDialogOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<'test' | 'live' | null>(null);

  // Form state
  const [freeCredits, setFreeCredits] = useState<number>(2);
  const [googlePayEnabled, setGooglePayEnabled] = useState(false);
  const [applePayEnabled, setApplePayEnabled] = useState(false);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response: PaymentSettingsResponse = await adminBillingApi.getPaymentSettings();
      setSettings(response.settings);
      setCanSwitchMode(response.canSwitchMode);

      // Initialize form state
      setFreeCredits(response.settings.freeCreditsForNewUsers);
      setGooglePayEnabled(response.settings.googlePayEnabled);
      setApplePayEnabled(response.settings.applePayEnabled);
      setSubscriptionsEnabled(response.settings.subscriptionsEnabled);
      setError(null);
    } catch (err) {
      setError('Failed to load payment settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updatedSettings = await adminBillingApi.updatePaymentSettings({
        freeCreditsForNewUsers: freeCredits,
        googlePayEnabled,
        applePayEnabled,
        subscriptionsEnabled,
      });

      setSettings(updatedSettings);
      setSuccess('Payment settings updated successfully');
    } catch (err) {
      setError('Failed to update payment settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleModeSwitch = (mode: 'test' | 'live') => {
    setPendingMode(mode);
    setModeSwitchDialogOpen(true);
  };

  const confirmModeSwitch = async () => {
    if (!pendingMode) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updatedSettings = await adminBillingApi.switchPaymentMode(pendingMode);
      setSettings(updatedSettings);
      setSuccess(`Payment mode switched to ${pendingMode.toUpperCase()}`);
      setModeSwitchDialogOpen(false);
      setPendingMode(null);
    } catch (err) {
      setError('Failed to switch payment mode');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = settings && (
    freeCredits !== settings.freeCreditsForNewUsers ||
    googlePayEnabled !== settings.googlePayEnabled ||
    applePayEnabled !== settings.applePayEnabled ||
    subscriptionsEnabled !== settings.subscriptionsEnabled
  );

  if (!isAdmin(user)) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        You do not have permission to view this page.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Payment Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure payment features and Stripe integration settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Payment Mode */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">Payment Mode</Typography>
          <Chip
            label={settings?.mode.toUpperCase()}
            color={settings?.mode === 'live' ? 'success' : 'warning'}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {settings?.mode === 'live'
            ? 'Live mode processes real payments. All transactions are real.'
            : 'Test mode uses Stripe test keys. No real payments are processed.'}
        </Typography>
        {canSwitchMode ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={settings?.mode === 'test' ? 'contained' : 'outlined'}
              onClick={() => handleModeSwitch('test')}
              disabled={settings?.mode === 'test' || saving}
            >
              Test Mode
            </Button>
            <Button
              variant={settings?.mode === 'live' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => handleModeSwitch('live')}
              disabled={settings?.mode === 'live' || saving}
            >
              Live Mode
            </Button>
          </Box>
        ) : (
          <Alert severity="info" icon={<WarningIcon />}>
            Only Super Admins can switch payment modes.
          </Alert>
        )}
      </Paper>

      {/* Feature Flags */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SubscriptionIcon color="primary" />
          <Typography variant="h6">Feature Flags</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enable or disable payment features across the platform.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={subscriptionsEnabled}
              onChange={(e) => setSubscriptionsEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography>Subscriptions</Typography>
              <Typography variant="caption" color="text.secondary">
                Enable recurring subscription billing for users
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={googlePayEnabled}
              onChange={(e) => setGooglePayEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GoogleIcon fontSize="small" />
              <Box>
                <Typography>Google Pay</Typography>
                <Typography variant="caption" color="text.secondary">
                  Allow users to pay with Google Pay
                </Typography>
              </Box>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={applePayEnabled}
              onChange={(e) => setApplePayEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AppleIcon fontSize="small" />
              <Box>
                <Typography>Apple Pay</Typography>
                <Typography variant="caption" color="text.secondary">
                  Allow users to pay with Apple Pay
                </Typography>
              </Box>
            </Box>
          }
          sx={{ alignItems: 'flex-start' }}
        />
      </Paper>

      {/* Credits Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CreditsIcon color="primary" />
          <Typography variant="h6">Credits Settings</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure credit allocation for new users.
        </Typography>

        <TextField
          label="Free Credits for New Users"
          type="number"
          value={freeCredits}
          onChange={(e) => setFreeCredits(parseInt(e.target.value) || 0)}
          inputProps={{ min: 0, max: 100 }}
          helperText="Number of credits automatically given to new users upon registration"
          sx={{ width: 300 }}
        />
      </Paper>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={fetchSettings}
          disabled={saving}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Mode Switch Confirmation Dialog */}
      <Dialog open={modeSwitchDialogOpen} onClose={() => setModeSwitchDialogOpen(false)}>
        <DialogTitle>
          Switch to {pendingMode?.toUpperCase()} Mode?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingMode === 'live' ? (
              <>
                <strong>Warning:</strong> Switching to LIVE mode will process real payments.
                All transactions will be charged to real payment methods.
                Make sure you have configured your live Stripe keys correctly.
              </>
            ) : (
              <>
                Switching to TEST mode will use Stripe test keys.
                No real payments will be processed. Use test card numbers for testing.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModeSwitchDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmModeSwitch}
            color={pendingMode === 'live' ? 'error' : 'primary'}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Switching...' : `Switch to ${pendingMode?.toUpperCase()}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
